<template>
  <div
    :class="classes"
    :data-qa="dataQa"
    @mouseenter="mouseenter"
    @mouseleave="mouseleave">
    <label
      v-if="label"
      class="s-textarea__label theme--light">
      <span
        class="mb-1 h8-text black--text"
        :data-qa="`${dataQa}-label`">{{ label }}</span>
    </label>
    <template v-if="type !== 'textarea'">
      <div
        v-if="$slots.prepend"
        class="s-input-group__prepend"
        @click="$emit('click-prepend')">
        <!-- @slot содержимое, добавляемое перед вводом, работает только при типе 'text' -->
        <slot name="prepend"/>
      </div>
      <input
        v-if="type !== 'textarea'"
        :ref="`input-${unicKey}`"
        :autocomplete="autocomplete"
        :autofocus="autofocus"
        class="s-input__inner"
        :class="`is-${size}`"
        :data-qa="`${dataQa}-input`"
        :disabled="inputDisabled"
        :name="`${dataQa}-input`"
        :placeholder="placeholder"
        :readonly="readonly"
        :tabindex="tabindex"
        :type="type"
        @blur="handleBlur"
        @change="handleChange"
        @click="$emit('click')"
        @compositionend="handleCompositionEnd"
        @compositionstart="handleCompositionStart"
        @compositionupdate="handleCompositionUpdate"
        @focus="handleFocus"
        @input="handleInput"
        @keydown="$emit('keydown', $event)"
        @keydown.enter="$emit('keydown_enter')"
        @keydown.esc="$emit('keydown_esc')"
        @paste="evt => $emit('paste', evt.clipboardData.getData('text/plain'))">
      <span
        v-if="$slots.prefix || prefixIcon"
        class="s-input__prefix"
        :data-qa="`${dataQa}-prefix`">
        <span
          class="s-input__prefix-inner"
          @click="$emit('clickPrefix')">
          <!-- @slot контент в качестве префикса ввода, работает только при типе 'text' -->
          <slot name="prefix"/>
          <sIcon
            v-if="prefixIcon"
            class="s-input__icon"
            line-height="38px"
            size="14"
            :type="prefixIcon"/>
        </span>
      </span>
      <span
        v-if="suffixVisible"
        class="s-input__suffix"
        :data-qa="`${dataQa}-suffix`">
        <span
          class="s-input__suffix-inner"
          @click="$emit('clickSuffix')">
          <template v-if="!showClear || !isWordLimitVisible">
            <!-- @slot контент как суффикс ввода, работает только при типе'text' -->
            <slot name="suffix"/>
            <sIcon
              v-if="suffixIcon"
              class="s-input__icon"
              line-height="38px"
              size="14"
              :type="suffixIcon"/>
          </template>
          <sButton
            v-if="showClear"
            class="s-input__clear"
            icon="cross"
            small
            type="text"
            @click.prevent.stop="clear"
            @mousedown.prevent/>
          <transition
            v-if="showWordLimit"
            name="s-zoom-in-top">
            <div
              v-show="isWordLimitVisible && textLength"
              class="s-input__count"
              :data-qa="`${dataQa}-textLength`">{{ textLength }}
              <template v-if="!!upperLimit">/{{ upperLimit }}</template>
            </div>
          </transition>
        </span>
      </span>
      <div
        v-if="$slots.append"
        class="s-input-group__append"
        @click="$emit('click-append')">
        <!-- @slot контент, добавляемый после ввода, работает только при типе 'text' -->
        <slot name="append"/>
      </div>
    </template>
    <template v-else>
      <textarea
        :ref="`textarea-${unicKey}`"
        :autofocus="autofocus"
        class="s-textarea__inner"
        :data-qa="`${dataQa}-textarea`"
        :disabled="inputDisabled"
        :placeholder="placeholder"
        :readonly="readonly"
        :style="textareaCalcStyle"
        :tabindex="tabindex"
        @blur="handleBlur"
        @change="handleChange"
        @compositionend="handleCompositionEnd"
        @compositionstart="handleCompositionStart"
        @compositionupdate="handleCompositionUpdate"
        @focus="handleFocus"
        @input="handleInput"
        @keydown="$emit('keydown', $event)"
        @keydown.enter="$emit('keydown_enter')"
        @keydown.esc="$emit('keydown_esc')"
        @paste="evt => $emit('paste', evt.clipboardData.getData('text/plain'))"/>
      <sButton
        v-if="showClear"
        class="s-input__clear"
        icon="cross"
        small
        type="text"
        @click.prevent.stop="clear"
        @mousedown.prevent/>
      <span
        v-if="isWordLimitVisible && textLength"
        class="s-input__count"
        :data-qa="`${dataQa}-textLength`">{{ textLength }}
        <template v-if="!!upperLimit">/{{ upperLimit }}</template>
      </span>
    </template>
  </div>
</template>
<script>
import Form from '../mixins/form.js'
import emitter from '../mixins/emitter'
import Migrating from '../mixins/migrating'
import calcTextareaHeight from '../utils/calcTextareaHeight'
import sIcon from './icon'
/**
 * Стандартный Input и textarea
 * @displayName Input/Textarea
 */
export default {
  name: 'sInput',
  componentName: 'sInput',
  mixins: [emitter, Migrating, Form],
  data() {
    return {
      height: 40,
      textareaCalcStyle: {},
      hovering: false,
      focused: false,
      isComposing: false,
      unicKey: (Math.random() + 1).toString(36).substring(7),
    }
  },
  props: {
    /**
     * Значение
     */
    value: {
      type: [String, Number],
    },
    /**
     * Признак блокировки
     */
    disabled: {
      type: Boolean,
    },
    /**
     * Проброс свойства placeholder
     * @ignore
     */
    placeholder: {
      type: [Number, String, Object],
    },
    /**
     * Только чтение
     */
    readonly: {
      type: Boolean,
    },
    /**
     * Тип поля
     * @values text, textarea
     */
    type: {
      type: String,
      default: 'text',
      validator: function (value) {
        const correct = ['text', 'textarea', 'email', 'url', 'tel'].indexOf(value) !== -1
        return correct
      },

    },
    /**
     * Количество строк (минимальное)
     */
    rows: {
      type: [String, Number],
      default: '5'
    },
    /**
     * Максимальное количество строк
     */
    maxRows: {
      type: [String, Number],
    },
    /**
     * Возможность очистить данные через кнопку
     */
    clearable: {
      type: Boolean,
      default: false
    },
    /**
     * Принудительное отображение кнопки очистки. Игнорирует признак `clearable` и `readonly` и `disabled`
     */
    clearForcible: {
      type: Boolean,
      default: false
    },
    /**
     * Максимальное количество символов
     */
    maxLength: {
      type: [String, Number],
      default: 4000
    },
    /**
     * Показывать количество введенных символов
     */
    showWordLimit: {
      type: Boolean,
      default: false
    },
    /**
     * Текст над полем ввода
     */
    label: {
      type: String,
    },
    /**
     * Не увеличивать при вводе текста
     */
    not_grow: {
      type: Boolean,
      default: false
    },
    /**
     * Иконка слева
     */
    prefixIcon: {
      type: String,
    },
    /**
     * Иконка справа
     */
    suffixIcon: {
      type: String,
    },
    /**
     * Проброс свойства autocomplete
     * @values on, of
     * @ignore
     */
    autocomplete: {
      type: String,
      default: 'off'
    },
    /**
     * Проброс свойства autofocus
     * @ignore
     */
    autofocus: Boolean,
    /**
     * Проброс свойства tabindex
     * @ignore
     */
    tabindex: String,
    /**
     * Проброс свойства ariaLabel
     * @ignore
     */
    ariaLabel: {
      type: String,
    },
    /**
     * запускать ли проверку формы. При использовании FormItem
     */
    validateEvent: {
      type: Boolean,
      default: true
    },
    /**
     * Проброс свойства form
     * @ignore
     */
    form: String,
    /**
     * Ключ для автотестов
     * @ignore
     */
    dataQa: {
      type: String,
      default: 'input'
    },
    /**
     * Прозрачный фон и границы
     */
    transparent: {
      type: Boolean,
      default: false
    },
    /**
     * задержка
     */
    debounce: {
      type: Boolean,
      default: false
    },
    /**
     * Удаляем переносы
     */
    removeNewLine: {
      type: Boolean,
      default: false
    },
    /**
     * Удаляем все спец символы
     */
    trim: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: 'normal',
      validator: function (value) {
        const correct = ['small', 'normal'].indexOf(value) !== -1
        return correct
      },
    },
  },
  computed: {
    classes() {
      let ignoreReadonly = false
      if (this.$parent?.$options?.componentName === 'sSelect') {
        ignoreReadonly = true
      }
      let result = [
        this.$attrs.className,
        this.type === 'textarea' ? 's-textarea' : 's-input',
        {
          'is-label': this.label,
          'is-disabled': this.inputDisabled,
          'is-readonly': this.readonly && !ignoreReadonly,
          'is-exceed': this.inputExceed,
          'is-transparent': this.transparent
        }
      ]
      if (this.type !== 'textarea') {
        if (this.$slots.prepend || this.$slots.append) {
          result.push('s-input-group')
        }
        if (this.$slots.append) {
          result.push('s-input-group--append')
        }
        if (this.$slots.prepend) {
          result.push('s-input-group--prepend')
        }
        if (this.$slots.prefix || this.prefixIcon) {
          result.push('s-input--prefix')
        }
        if (this.$slots.suffix || this.suffixIcon || this.clearForcible || this.clearable) {
          result.push('s-input--suffix')
        }
      } else {
        if (this.isWordLimitVisible && this.textLength && this.showClear && this.height < 41) {
          result.push('s-textarea--showClear')
          result.push('s-textarea--wordLimit')
        }
      }
      return result
    },
    suffixVisible() {
      return this.$slots.suffix ||
        this.suffixIcon ||
        this.showClear ||
        this.isWordLimitVisible
    },
    nativeInputValue() {
      return this.value === null || this.value === undefined ? '' : String(this.value)
    },
    showClear() {
      const needUseClear = this.clearForcible || (this.clearable &&
        !this.inputDisabled &&
        !this.readonly)

      return needUseClear &&
        this.nativeInputValue &&
        (this.focused || this.hovering)
    },
    isWordLimitVisible() {
      return this.showWordLimit &&
        (this.type === 'text' || this.type === 'textarea') &&
        !this.inputDisabled &&
        !this.readonly &&
        this.focused
    },
    upperLimit() {
      return this.maxLength ? Number(this.maxLength) : null
    },
    textLength() {
      if (typeof this.value === 'number') {
        return String(this.value).length
      }
      return (this.value || '').length
    },
    inputExceed() {
      return this.isWordLimitVisible && this.maxLength && (this.textLength > this.upperLimit)
    }
  },
  watch: {
    value(val) {
      if (this.validateEvent) {
        this.dispatch('sFormItem', 's.form.change', [val])
      }
      this.$nextTick(this.resizeTextarea)
    },
    nativeInputValue() {
      this.setNativeInputValue()
    },
    type() {
      this.$nextTick(() => {
        this.setNativeInputValue()
        this.resizeTextarea()
        this.updateIconOffset()
      })
    }
  },
  methods: {
    mouseenter() {
      this.hovering = true
      this.$emit('mouseenter')
    },
    mouseleave() {
      this.hovering = false
      this.$emit('mouseleave')
    },
    focus() {
      this.getInput().focus()
    },
    blur() {
      this.getInput().blur()
    },
    handleBlur(event) {
      this.focused = false
      this.$emit('blur', event)
      if (this.validateEvent) {
        this.dispatch('sFormItem', 's.form.blur', [this.value])
      }
    },
    select() {
      this.getInput().select()
    },
    resizeTextarea() {
      if (this.$isServer) { return }
      if (this.type !== 'textarea') { return }
      const rows = this.rows ? Number(this.rows) : 5
      const maxRows = this.maxRows ? Number(this.maxRows) : 100
      if (this.not_grow) {
        this.textareaCalcStyle = {
          minHeight: calcTextareaHeight(this.getInput(), rows).style.minHeight,
        }
        return
      }
      const textareaProp = calcTextareaHeight(this.getInput(), rows, maxRows < rows ? null : maxRows)
      this.height = textareaProp.height
      this.textareaCalcStyle = textareaProp.style
    },
    setNativeInputValue() {
      const input = this.getInput()
      if (!input) { return }
      if (input.value === this.nativeInputValue) { return }
      input.value = this.nativeInputValue
    },
    handleFocus(event) {
      this.focused = true
      this.$emit('focus', event)
    },
    handleCompositionStart() {
      this.isComposing = true
    },
    handleCompositionUpdate() {
      this.isComposing = false
    },
    handleCompositionEnd(event) {
      if (this.isComposing) {
        this.isComposing = false
        this.handleInput(event)
      }
    },
    parseText(text) {
      let result = text
      if (this.removeNewLine) {
        result = result.replace(/[\r\n]/gm, '')
      }
      if (this.trim) {
        result = result.trim()
      }

      return result
    },
    handleInput(event) {
      if (this.isComposing) { return }
      const result = this.parseText(event.target.value)
      if (result === this.nativeInputValue) { return }
      this.$emit('change', result)
      this.$emit('update:value', result)
      this.$emit('input', result)
      this.$nextTick(this.setNativeInputValue)
    },
    handleChange(event) {
      const result = this.parseText(event.target.value)
      this.$emit('change', result)
      this.$emit('update:value', result)
    },
    calcIconOffset(place) {
      let elList = [].slice.call(this.$el.querySelectorAll(`.s-input__${place}`) || [])
      if (!elList.length) { return }
      let el = null
      for (let i = 0; i < elList.length; i++) {
        if (elList[i].parentNode === this.$el) {
          el = elList[i]
          break
        }
      }
      if (!el) { return }
      const pendantMap = {
        suffix: 'append',
        prefix: 'prepend'
      }
      const pendant = pendantMap[place]
      if (this.$slots[pendant]) {
        el.style.transform = `translateX(${place === 'suffix' ? '-' : ''}${this.$el.querySelector(`.s-input-group__${pendant}`).offsetWidth}px)`
      } else {
        el.removeAttribute('style')
      }
    },
    updateIconOffset() {
      this.calcIconOffset('prefix')
      this.calcIconOffset('suffix')
    },
    clear() {
      this.$emit('change', '')
      this.$emit('update:value', '')
      this.$emit('input', '')
      /**
      * Данные были очищены
      * @event clear
      */
      this.$emit('clear')
    },
    getInput() {
      return this.$refs[`input-${this.unicKey}`] || this.$refs[`textarea-${this.unicKey}`]
    },
  },
  created() {
    this.$on('inputSelect', this.select)
  },
  mounted() {
    this.setNativeInputValue()
    this.updateIconOffset()
    this.$nextTick(() => {
      this.resizeTextarea()
    })
  },
  updated() {
    this.$nextTick(this.updateIconOffset)
  },
  components: {
    sIcon
  }
}
</script>
